import * as Sentry from '@sentry/react';
import { Feature } from 'admin/src/enums';
import { Apply, Documents, Home, LmsCoursePlayer, MyTraining, News, RequiredClasses, ScheduledClasses, WorkSchedules } from 'pollworker/src/components';
import AvailabilitySurvey from 'pollworker/src/components/AvailabilitySurvey';
import PollworkerTimeclock from 'pollworker/src/components/PollworkerTimeclock';
import Profile from 'pollworker/src/components/Profile';
import Registration from 'pollworker/src/components/Registration';
import useIsLoggedIn from 'pollworker/src/components/selectors/useIsLoggedIn';
import { LOCAL_STORAGE_KEYS } from 'pollworker/src/constants';
import { getCurrentCustomer, getCurrentUser, getWebsiteUser } from 'pollworker/src/fetchers';
import { useFeatureFlagState } from 'pollworker/src/hooks';
import ForgotPassword from 'pollworker/src/screens/ForgotPassword';
import Login from 'pollworker/src/screens/Login';
import ResetPassword from 'pollworker/src/screens/ResetPassword';
import { setCurrentCustomer, setCurrentPollworkerUser } from 'pollworker/src/state/actions';
import React, { type FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTimeoutFn, useTitle } from 'react-use';
import { Loading } from 'shared/src/components';
import { SectionSubHeading, SectionSubSubHeading } from 'shared/src/components/SectionHeading';
import { Button } from 'shared/src/components/ui/new';
import useTheme from 'shared/src/hooks/useTheme';
import * as SentrySetup from 'shared/src/setupSentry';
import { Link, Redirect, Route, Switch } from 'wouter';

function LmsProtectedRoute({ component: Component, disabledRedirect }: { component: FC; disabledRedirect: string }) {
  const loggedIn = useIsLoggedIn();
  const { enabled, loading } = useFeatureFlagState(null, Feature.POLLWORKER_LMS);

  if (!loggedIn) return <Redirect to="/login" />;
  if (loading) return <Loading loadingMessage="Please wait..." />;
  if (!enabled) return <Redirect to={disabledRedirect} />;

  return <Component />;
}

export default function App() {
  const [showApp, setShowApp] = useState<boolean>();
  const [showReload, setShowReload] = useState(false);
  const loggedIn = useIsLoggedIn();
  const dispatch = useDispatch();

  useTitle('EasyVote PollWorker');
  useTheme('ev');

  const [, cancelShowReloadTimer] = useTimeoutFn(() => {
    Sentry.captureMessage('Long load, showing reload button');
    setShowReload(true);
  }, 5000);

  useEffect(() => {
    handleInit();
  }, []);

  async function handleInit() {
    try {
      const websiteUserResp = await getWebsiteUser();
      window.localStorage.setItem(LOCAL_STORAGE_KEYS.WEBSITE_USER, JSON.stringify(websiteUserResp));

      const currentUserId = window.localStorage.getItem('CurrentUser');
      const currentUserResp = currentUserId ? await getCurrentUser(currentUserId) : await getCurrentCustomer();
      if (!currentUserResp) {
        cancelShowReloadTimer();
        return;
      }
      dispatch(setCurrentCustomer(currentUserResp));

      if (currentUserId) {
        dispatch(setCurrentPollworkerUser(currentUserResp));
        SentrySetup.identifyLoggedInUser(
          currentUserResp.EVUserId,
          currentUserResp.UserId,
          '',
          currentUserResp.Customer.Id,
          currentUserResp.Customer.Name,
          currentUserResp.Customer.State,
        );
      }

      cancelShowReloadTimer();

      setShowApp(true);
    } catch (e) {
      setShowReload(true);
      setShowApp(false);
      Sentry.captureException(e);
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    }
  }

  function protectRoute(component: FC): FC {
    if (loggedIn) return component;

    return () => <Redirect to="/login" />;
  }

  if (!showApp) {
    return (
      <div className="min-h-96 w-full h-full flex flex-col justify-center space-y-4">
        <Loading loadingMessage={showReload ? 'Still loading...' : 'Please wait...'} />
        {showReload ? (
          <span className="mx-auto">
            <Button size="sm" onClick={() => window.location.reload()}>
              Reload?
            </Button>
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/apply" component={Apply} />
      <Route path="/account/apply" component={Apply} />
      <Route path="/availability/survey/:scheduleSurveyId/:evUserId" component={AvailabilitySurvey} />
      <Route path="/account/createcredentials/:evUserId" component={Registration} />

      <Route path="/login" component={Login} />
      <Route path="/login/forgot" component={ForgotPassword} />
      <Route path="/login/reset" component={ResetPassword} />

      <Route path="/account/resetpassword">
        <Redirect to="/login/forgot" />
      </Route>

      <Route path="/home" component={protectRoute(Home)} />
      <Route path="/profile" component={protectRoute(Profile)} />
      <Route path="/news" component={protectRoute(News)} />
      <Route path="/documents" component={protectRoute(Documents)} />
      <Route path="/work_schedule/:viewName?" component={protectRoute(WorkSchedules)} />
      <Route path="/training">
        <LmsProtectedRoute component={MyTraining} disabledRedirect="/" />
      </Route>
      <Route path="/training/courses/:courseUuid">
        <LmsProtectedRoute component={LmsCoursePlayer} disabledRedirect="/missing" />
      </Route>
      <Route path="/courses/:courseUuid">{(params) => <Redirect replace to={`/training/courses/${encodeURIComponent(params.courseUuid)}${window.location.search}`} />}</Route>
      <Route path="/classes/required" component={protectRoute(RequiredClasses)} />
      <Route path="/classes/scheduled" component={protectRoute(ScheduledClasses)} />
      <Route path="/schedule/timeclock" component={protectRoute(PollworkerTimeclock)} />

      <Route>
        <div className="text-center my-40">
          <h1 className="font-bold text-gray-700" style={{ fontSize: '5em' }}>
            Uh oh!
          </h1>
          <SectionSubHeading>Hmmm... not sure about that. 404.</SectionSubHeading>
          <br />
          <SectionSubSubHeading>
            <Link to={loggedIn ? '/news' : '/'}>← Head back</Link>
          </SectionSubSubHeading>
        </div>
      </Route>
    </Switch>
  );
}
